import React, { useState, useEffect } from 'react';
import { formatTime } from '../../utils/dateFormat';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import {
  ArrowLeft, Calendar, Clock, MapPin, Users, CheckCircle2, ArrowRight,
  Loader2, ShoppingCart, Star, Award, Lock, Globe, School, AlertCircle, Check, X,
  Package, Truck, Building, ExternalLink, BookOpen
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { KitDetailsSection } from '../../components/KitContentsPreview';
import { toast } from 'sonner';
import axios from 'axios';
import { trackAddToCart, trackViewItem } from '../../utils/ga4';
import { useBotShield } from '../../hooks/useBotShield';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* Notify Me / Express Interest Button */
function NotifyMeButton({ courseId, flavor, source = 'notify_me_camp', successMsg = "✓ We'll notify you when sessions are posted!" }) {
  const [email, setEmail] = React.useState('');
  const [submitted, setSubmitted] = React.useState(false);
  const { HoneypotField, getPayloadFields } = useBotShield();
  const handleNotify = async () => {
    if (!email) return;
    try {
      await fetch(`${API_URL}/api/contact/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: 'Notify Me', email,
          subject: `Notify: ${flavor} sessions`,
          message: `Course: ${courseId}\nFlavor: ${flavor}\nEmail: ${email}`,
          source,
          ...getPayloadFields(),
        }),
      });
    } catch {}
    setSubmitted(true);
  };
  if (submitted) return <p className="text-xs text-green-600 font-semibold">{successMsg}</p>;
  return (
    <div className="flex gap-2 justify-center max-w-xs mx-auto">
      <HoneypotField />
      <input type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)}
        className="flex-1 px-3 py-1.5 text-xs rounded-md border border-gray-200 focus:outline-none focus:border-primary" />
      <button onClick={handleNotify} className="px-3 py-1.5 text-xs font-semibold bg-primary text-white rounded-md hover:bg-primary/90">Notify Me</button>
    </div>
  );
}

const fmtD = iso => iso
  ? new Date(iso + 'T00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : '';

const fmtDShort = iso => iso
  ? new Date(iso + 'T00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  : '';

const sectionLabel = (s) => {
  const prefix = s.delivery_type === 'online' ? 'Online' : '';
  const start  = fmtDShort(s.start_date);
  const end    = fmtDShort(s.end_date);
  const dates  = start && end ? `${start} – ${end}` : start;
  const days   = Array.isArray(s.meeting_days) && s.meeting_days.length
    ? s.meeting_days.map(d => d.slice(0, 3)).join(', ')
    : '';
  const status = s.enrolled_count >= s.capacity ? ' · Full' : '';
  return [prefix, dates, days].filter(Boolean).join(' · ') + status;
};

export default function CourseDetailPage() {
  const { courseId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const preselectedSectionId = searchParams.get('section');
  const navigate = useNavigate();
  const { isAuthenticated, api, user } = useAuth();
  const { addToCart } = useCart();

  const [course, setCourse] = useState(null);
  const [sections, setSections] = useState([]);
  const [learners, setLearners] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedLearners, setSelectedLearners] = useState([]);
  const [addingToCart, setAddingToCart] = useState(false);
  const [eligibility, setEligibility] = useState({});
  const [includeKit, setIncludeKit] = useState(false);
  const [kitDeliveryMethod, setKitDeliveryMethod] = useState('pickup');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [courseRes, sectionsRes] = await Promise.all([
          axios.get(`${API_URL}/api/courses/${courseId}`),
          axios.get(`${API_URL}/api/sections/`, { params: { course: courseId } }),
        ]);
        setCourse(courseRes.data);
        const sectionsData = sectionsRes.data.results || sectionsRes.data;
        const courseSections = sectionsData.filter(s => s.course === courseId || s.course_id === courseId);
        let activeSections = courseSections.length > 0 ? courseSections : sectionsData;

        const todayIso = new Date().toISOString().slice(0, 10);
        activeSections = activeSections.filter(s => {
          const endGate = s.end_date || s.start_date;
          return !endGate || endGate >= todayIso;
        });

        if (preselectedSectionId) {
          const found = activeSections.find(s => s.id === preselectedSectionId);
          if (found) {
            setSelectedSection(preselectedSectionId);
            setIncludeKit(found.kit_requirement === 'required');
          } else {
            // May be a private session — fetch it directly by ID
            try {
              const privateRes = await axios.get(`${API_URL}/api/sections/${preselectedSectionId}/`);
              if (privateRes.data?.is_private) {
                activeSections = [...activeSections, privateRes.data];
                setSelectedSection(preselectedSectionId);
                setIncludeKit(privateRes.data.kit_requirement === 'required');
              }
            } catch {}
          }
        }

        setSections(activeSections);

        try {
          const firstSection = (courseSections.length > 0 ? courseSections : sectionsData)[0];
          trackViewItem({
            id: courseRes.data.id || courseId,
            name: courseRes.data.title || courseRes.data.name,
            category: courseRes.data.category || 'After-School Program',
            location: firstSection?.location_name || null,
            grade_band: courseRes.data.grade_band,
            price: Number(firstSection?.price || courseRes.data.price || 0),
          });
        } catch { /* analytics never breaks UX */ }

        axios.get(`${API_URL}/api/courses/${courseId}/lessons/`)
          .then(r => setLessons(r.data || []))
          .catch(() => {});

        if (isAuthenticated && user?.role === 'parent') {
          try {
            const learnersRes = await api.get('/learners/');
            const learnersData = learnersRes.data.results || learnersRes.data;
            setLearners(learnersData);

            if (learnersData.length > 0 && courseSections.length > 0) {
              const sectionIds = courseSections.map(s => s.id);
              const learnerIds = learnersData.map(l => l.id);

              try {
                const eligibilityRes = await api.post('/sections/check_eligibility_bulk/', {
                  section_ids: sectionIds,
                  learner_ids: learnerIds
                });
                setEligibility(eligibilityRes.data);
              } catch (e) {
                console.error('Failed to check eligibility:', e);
              }
            }
          } catch (e) {
            console.error('Failed to fetch learners:', e);
          }
        }
      } catch (error) {
        console.error('Failed to fetch course:', error);
        toast.error('Failed to load course details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [courseId, isAuthenticated, api, user, preselectedSectionId]);

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to enroll');
      navigate('/login', { state: { from: `/courses/${courseId}` } });
      return;
    }

    if (!selectedSection || selectedLearners.length === 0) {
      toast.error('Please select a session and at least one learner');
      return;
    }

    const section = sections.find(s => s.id === selectedSection);
    const sectionKitReq = section?.kit_requirement;
    const shouldIncludeKit = sectionKitReq === 'required' || (course.has_kit && includeKit);
    const deliveryMethod = section?.delivery_type === 'online' ? 'ship' : kitDeliveryMethod;

    setAddingToCart(true);
    try {
      for (const learnerId of selectedLearners) {
        const sectionEligibility = eligibility[selectedSection];
        if (sectionEligibility) {
          const learnerEligibility = sectionEligibility.learners?.[learnerId];
          if (learnerEligibility && !learnerEligibility.eligible) {
            const learner = learners.find(l => l.id === learnerId);
            toast.error(`${learner?.first_name || 'Learner'} is not eligible: ${learnerEligibility.reason}`);
            continue;
          }
        }
        await addToCart(selectedSection, learnerId, shouldIncludeKit, deliveryMethod);
        try {
          trackAddToCart({
            id: selectedSection,
            name: section?.section_title || course?.title || course?.name,
            category: course?.category || 'After-School Program',
            location: section?.location_name || null,
            grade_band: course?.grade_band,
            price: Number(section?.price || course?.price || 0),
          });
        } catch { /* analytics never breaks UX */ }
      }
    } catch (error) {
      // Error handled in cart context
    } finally {
      setAddingToCart(false);
    }
  };

  const handleLearnerToggle = (learnerId, isEligible) => {
    if (!isEligible) return;

    setSelectedLearners(prev => {
      if (prev.includes(learnerId)) {
        return prev.filter(id => id !== learnerId);
      } else {
        return [...prev, learnerId];
      }
    });
  };

  const getRestrictionIcon = (section) => {
    if (section.enrollment_restriction === 'school_only') {
      return <School className="h-4 w-4 text-blue-600" />;
    }
    if (section.enrollment_restriction === 'district_only') {
      return <Lock className="h-4 w-4 text-purple-600" />;
    }
    return <Globe className="h-4 w-4 text-green-600" />;
  };

  const getRestrictionBadge = (section) => {
    if (section.enrollment_restriction === 'school_only') {
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs">
          <School className="h-3 w-3 mr-1" />
          {section.restricted_to_school_name || 'School Only'}
        </Badge>
      );
    }
    if (section.enrollment_restriction === 'district_only') {
      return (
        <Badge variant="outline" className="bg-purple-50 text-purple-700 text-xs">
          <Lock className="h-3 w-3 mr-1" />
          {section.restricted_to_district_name || 'District Only'}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-green-50 text-green-700 text-xs">
        <Globe className="h-3 w-3 mr-1" />
        Open Enrollment
      </Badge>
    );
  };

  const getLearnerEligibilityForSection = (sectionId, learnerId) => {
    const sectionData = eligibility[sectionId];
    if (!sectionData || !sectionData.learners) return null;
    return sectionData.learners[learnerId];
  };

  const getEligibleLearnersForSection = (sectionId) => {
    const sectionData = eligibility[sectionId];
    if (!sectionData || !sectionData.learners) return learners;
    return learners.filter(l => {
      const elig = sectionData.learners[l.id];
      return !elig || elig.eligible;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="container-app py-12 text-center">
        <h1 className="text-2xl font-bold mb-4">Course not found</h1>
        <Link to="/catalog">
          <Button>Back to Catalog</Button>
        </Link>
      </div>
    );
  }

  const selectedSectionData = sections.find(s => s.id === selectedSection);
  const eligibleLearners = selectedSection ? getEligibleLearnersForSection(selectedSection) : learners;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">

      {/* Light header */}
      <section className="bg-white border-b border-gray-200">
        <div className="container-app py-6 md:py-8">
          <Link
            to="/catalog"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-800 mb-5 transition-colors"
            data-testid="back-to-catalog"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Back to Catalog
          </Link>

          <div className="flex flex-wrap gap-2 mb-3">
            <Badge className="bg-yellow-400 text-yellow-900 hover:bg-yellow-400 capitalize font-semibold">
              {course.skill_level}
            </Badge>
            {course.grade_bands?.map(grade => (
              <Badge key={grade} variant="outline" className="text-gray-700">{grade}</Badge>
            ))}
            {course.category && (
              <Badge variant="outline" className="text-gray-700 capitalize">{course.category}</Badge>
            )}
          </div>

          <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-testid="course-title">
            {course.title}
          </h1>
          <p className="text-base text-gray-600 max-w-2xl leading-relaxed">
            {course.description}
          </p>

          {sections.length > 0 && (
            <div className="flex items-center gap-2 mt-4 text-sm text-gray-500">
              <Calendar className="h-4 w-4" />
              <span>{sections.length} session{sections.length !== 1 ? 's' : ''} available</span>
            </div>
          )}
        </div>
      </section>

      {/* Two-column body */}
      <section className="section-padding">
        <div className="container-app">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

            {/* Left column: course content */}
            <div className="lg:col-span-2 space-y-6">

              {/* Thumbnail */}
              {course.thumbnail_url && (
                <img
                  src={course.thumbnail_url}
                  alt={course.title}
                  className="rounded-2xl w-full aspect-video object-cover shadow-md"
                />
              )}

              {/* What You'll Learn */}
              {course.learning_outcomes?.length > 0 && (
                <Card className="card-dashboard">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Award className="h-5 w-5 text-primary" />
                      What You'll Learn
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {course.learning_outcomes.map((outcome, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm" data-testid={`outcome-${index}`}>
                          <CheckCircle2 className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                          <span>{outcome}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Session Outline — only shown when populated */}
              {lessons.length > 0 && (
                <Card className="card-dashboard" data-testid="course-lessons">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <BookOpen className="h-5 w-5 text-blue-600" />
                      Session Outline ({lessons.length} sessions)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {lessons.map((lesson) => (
                        <div key={lesson.id} className="border-l-2 border-blue-200 pl-3" data-testid={`lesson-${lesson.lesson_number}`}>
                          <p className="text-sm font-semibold text-gray-900">
                            <span className="text-blue-500 mr-1">{lesson.lesson_number}.</span>
                            {lesson.title}
                          </p>
                          {lesson.objectives?.length > 0 && (
                            <ul className="mt-1 space-y-0.5">
                              {lesson.objectives.map((obj, i) => (
                                <li key={i} className="text-xs text-gray-500 flex items-start gap-1">
                                  <CheckCircle2 className="h-3 w-3 text-blue-400 flex-shrink-0 mt-0.5" />
                                  {obj}
                                </li>
                              ))}
                            </ul>
                          )}
                          {lesson.materials?.length > 0 && (
                            <p className="text-[10px] text-gray-400 mt-1">
                              Materials: {lesson.materials.join(', ')}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Skills Covered — only shown when populated */}
              {course.rubric_items?.length > 0 && (
                <Card className="card-dashboard">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Star className="h-5 w-5 text-secondary" />
                      Skills Covered
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {course.rubric_items.map((item, index) => (
                        <Badge key={index} variant="secondary" className="text-sm" data-testid={`skill-${index}`}>
                          {item}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right column: sticky enrollment card */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <Card className="card-dashboard shadow-md">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Enroll Now</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {sections.length === 0 ? (
                      <div className="text-center py-6 space-y-3">
                        <p className="text-sm text-muted-foreground">No sessions are currently available for this course.</p>
                        <NotifyMeButton courseId={courseId} flavor={course.title} />
                      </div>
                    ) : (
                      <>
                        {/* Session Selection — visible to all users */}
                        <div>
                          <label className="text-sm font-medium mb-2 block">Select Session</label>
                          <Select value={selectedSection} onValueChange={(v) => {
                            setSelectedSection(v);
                            setSelectedLearners([]);
                            setSearchParams({ section: v }, { replace: true });
                            const section = sections.find(s => s.id === v);
                            const sectionKitReq = section?.kit_requirement;
                            if (sectionKitReq === 'required') {
                              setIncludeKit(true);
                            } else {
                              setIncludeKit(false);
                            }
                          }}>
                            <SelectTrigger data-testid="section-select">
                              <SelectValue placeholder="Choose a session" />
                            </SelectTrigger>
                            <SelectContent>
                              {sections.map(section => (
                                <SelectItem key={section.id} value={section.id}>
                                  {sectionLabel(section)}{section.location_type === 'park_district' ? ' ↗' : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Session details — visible to all users */}
                        {selectedSectionData && (
                          <div className="text-xs text-muted-foreground space-y-1 bg-gray-50 rounded-lg px-3 py-2">
                            <div className="flex items-center gap-1.5">
                              <MapPin className="h-3 w-3 flex-shrink-0" />
                              <span>{selectedSectionData.location_name}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Calendar className="h-3 w-3 flex-shrink-0" />
                              <span>{fmtD(selectedSectionData.start_date)} – {fmtD(selectedSectionData.end_date)}</span>
                            </div>
                            {selectedSectionData.meeting_days && (
                              <div className="flex items-center gap-1.5">
                                <Calendar className="h-3 w-3 flex-shrink-0" />
                                <span>{Array.isArray(selectedSectionData.meeting_days) ? selectedSectionData.meeting_days.join(', ') : selectedSectionData.meeting_days}</span>
                              </div>
                            )}
                            {selectedSectionData.start_time && (
                              <div className="flex items-center gap-1.5">
                                <Clock className="h-3 w-3 flex-shrink-0" />
                                <span>{formatTime(selectedSectionData.start_time)} – {formatTime(selectedSectionData.end_time)}</span>
                              </div>
                            )}
                            {getRestrictionBadge(selectedSectionData)}
                          </div>
                        )}

                        {/* Price summary — visible to all users, kit-aware for authenticated */}
                        {selectedSectionData && (
                          <div className="bg-gray-50 rounded-lg px-4 py-3 space-y-1.5">
                            <div className="flex justify-between text-sm text-gray-600">
                              <span>Course</span>
                              <span>${(Number(selectedSectionData.price) || 0).toFixed(2)}</span>
                            </div>
                            {course.has_kit && (selectedSectionData?.kit_requirement === 'required' || includeKit) && (
                              <div className="flex justify-between text-sm text-primary">
                                <span>+ STEM Kit</span>
                                <span>${(Number(course.kit_details?.price) || 0).toFixed(2)}</span>
                              </div>
                            )}
                            <div className="flex justify-between font-bold text-base pt-1.5 border-t border-gray-200">
                              <span>Total</span>
                              <span>${(
                                (Number(selectedSectionData.price) || 0) +
                                ((course.has_kit && (selectedSectionData?.kit_requirement === 'required' || includeKit))
                                  ? (Number(course.kit_details?.price) || 0)
                                  : 0)
                              ).toFixed(2)}</span>
                            </div>
                          </div>
                        )}

                        {/* Auth gate: show login/add-learner CTA or full enrollment UI */}
                        {!isAuthenticated ? (
                          <div className="text-center py-2">
                            <p className="text-sm text-muted-foreground mb-3">Log in to enroll your child</p>
                            <Link to="/login" state={{ from: `/courses/${courseId}` }}>
                              <Button className="w-full" data-testid="login-to-enroll-btn">Log In to Enroll</Button>
                            </Link>
                          </div>
                        ) : learners.length === 0 ? (
                          <div className="text-center py-2">
                            <p className="text-sm text-muted-foreground mb-3">Add a learner to enroll</p>
                            <Link to="/parent/learners">
                              <Button className="w-full">Add Learner</Button>
                            </Link>
                          </div>
                        ) : (
                          <>
                        {/* Learner Selection */}
                        {selectedSection && (
                          <div>
                            <label className="text-sm font-medium mb-3 block">Select Children to Enroll</label>
                            <div className="space-y-3">
                              {learners.map(learner => {
                                const elig = getLearnerEligibilityForSection(selectedSection, learner.id);
                                const isEligible = !elig || elig.eligible;
                                const isSelected = selectedLearners.includes(learner.id);

                                return (
                                  <div
                                    key={learner.id}
                                    className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                                      isSelected
                                        ? 'border-primary bg-primary/5'
                                        : isEligible
                                          ? 'border-border hover:border-primary/50 cursor-pointer'
                                          : 'border-border bg-muted/30'
                                    }`}
                                    onClick={() => handleLearnerToggle(learner.id, isEligible)}
                                    data-testid={`learner-option-${learner.id}`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <Checkbox
                                        id={`learner-${learner.id}`}
                                        checked={isSelected}
                                        disabled={!isEligible}
                                        onCheckedChange={() => handleLearnerToggle(learner.id, isEligible)}
                                        onClick={(e) => e.stopPropagation()}
                                        data-testid={`learner-checkbox-${learner.id}`}
                                      />
                                      <div>
                                        <Label
                                          htmlFor={`learner-${learner.id}`}
                                          className={`font-medium cursor-pointer ${!isEligible ? 'text-muted-foreground' : ''}`}
                                        >
                                          {learner.first_name} {learner.last_name}
                                        </Label>
                                        {learner.grade && (
                                          <p className="text-xs text-muted-foreground">Grade {learner.grade}</p>
                                        )}
                                      </div>
                                    </div>

                                    {isEligible ? (
                                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                        Eligible
                                      </Badge>
                                    ) : (
                                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                                        Ineligible
                                      </Badge>
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                            {learners.some(l => {
                              const elig = getLearnerEligibilityForSection(selectedSection, l.id);
                              return elig && !elig.eligible;
                            }) && (
                              <Alert className="mt-3 bg-amber-50 border-amber-200">
                                <AlertCircle className="h-4 w-4 text-amber-600" />
                                <AlertDescription className="text-amber-700 text-sm">
                                  Some children are ineligible due to school/district restrictions for this session.
                                </AlertDescription>
                              </Alert>
                            )}
                          </div>
                        )}

                        {/* Kit selection */}
                        {selectedSection && course.has_kit && selectedSectionData?.kit_requirement !== 'none' && selectedSectionData?.kit_requirement !== 'included' && (
                          <div className="border border-border rounded-lg p-4 space-y-3" data-testid="kit-selection">
                            <div className="flex items-start gap-3">
                              <Package className="h-5 w-5 text-primary mt-0.5" />
                              <div className="flex-1">
                                <h4 className="font-medium">{course.kit_details?.name || 'Product'}</h4>
                                {selectedSectionData?.kit_requirement === 'required' ? (
                                  <div className="space-y-1">
                                    <Badge className="mt-1 bg-blue-100 text-blue-700">Required for this session</Badge>
                                    <p className="text-xs text-muted-foreground">
                                      This product is required and will be added to your order automatically.
                                    </p>
                                  </div>
                                ) : (
                                  <Badge variant="outline" className="mt-1">Optional Add-on</Badge>
                                )}
                              </div>
                              <div className="text-right">
                                <span className="font-bold text-primary">
                                  +${(Number(course.kit_details?.price) || 0).toFixed(2)}
                                </span>
                                {selectedSectionData?.kit_requirement === 'required' && (
                                  <p className="text-xs text-muted-foreground">added to total</p>
                                )}
                              </div>
                            </div>

                            {selectedSectionData?.kit_requirement === 'optional' && (
                              <div className="flex items-center space-x-2">
                                <Checkbox
                                  id="include-kit"
                                  checked={includeKit}
                                  onCheckedChange={setIncludeKit}
                                  data-testid="include-kit-checkbox"
                                />
                                <Label htmlFor="include-kit" className="text-sm cursor-pointer">
                                  Add {course.kit_details?.name || 'product'} to my order
                                </Label>
                              </div>
                            )}

                            {(selectedSectionData?.kit_requirement === 'required' || includeKit) && selectedSectionData?.delivery_type !== 'online' && (
                              <div className="space-y-2 pt-2 border-t border-border">
                                <Label className="text-sm font-medium">Kit Delivery</Label>
                                <RadioGroup
                                  value={kitDeliveryMethod}
                                  onValueChange={setKitDeliveryMethod}
                                  className="flex flex-col gap-2"
                                >
                                  <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="pickup" id="pickup" data-testid="delivery-pickup" />
                                    <Label htmlFor="pickup" className="flex items-center gap-2 text-sm cursor-pointer">
                                      <Building className="h-4 w-4" />
                                      Pick up at class location
                                    </Label>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="ship" id="ship" data-testid="delivery-ship" />
                                    <Label htmlFor="ship" className="flex items-center gap-2 text-sm cursor-pointer">
                                      <Truck className="h-4 w-4" />
                                      Ship to my address
                                    </Label>
                                  </div>
                                </RadioGroup>
                              </div>
                            )}

                            {(selectedSectionData?.kit_requirement === 'required' || includeKit) && selectedSectionData?.delivery_type === 'online' && (
                              <Alert className="bg-blue-50 border-blue-200">
                                <Truck className="h-4 w-4 text-blue-600" />
                                <AlertDescription className="text-blue-700 text-sm">
                                  Kit will be shipped to your address for this online course.
                                </AlertDescription>
                              </Alert>
                            )}
                          </div>
                        )}

                        {/* Kit included banner */}
                        {selectedSection && course.has_kit && selectedSectionData?.kit_requirement === 'included' && (
                          <div className="border border-green-200 bg-green-50 rounded-lg p-4 flex items-center gap-3" data-testid="product-included-banner">
                            <Package className="h-5 w-5 text-green-600" />
                            <div>
                              <p className="font-medium text-green-800">{course.kit_details?.name || 'Product'} Included</p>
                              <p className="text-xs text-green-600">Take-home product included with this course at no extra charge.</p>
                            </div>
                          </div>
                        )}

                        {/* Register button */}
                        {selectedSectionData?.location_type === 'park_district' ? (
                          selectedSectionData?.location_external_purchase_url ? (
                            <a
                              href={selectedSectionData.location_external_purchase_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block"
                            >
                              <Button
                                className="w-full bg-emerald-600 hover:bg-emerald-700"
                                size="lg"
                                disabled={!selectedSection}
                                data-testid="external-purchase-btn"
                              >
                                <ExternalLink className="h-4 w-4 mr-2" />
                                Register at {selectedSectionData.location_name || 'Park District'}
                              </Button>
                              <p className="text-xs text-muted-foreground text-center mt-1.5">
                                Registration is handled by {selectedSectionData.location_name}
                              </p>
                            </a>
                          ) : (
                            <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-center">
                              <p className="text-sm font-semibold text-emerald-800">Register through the park district</p>
                              <p className="text-xs text-emerald-600 mt-1">Contact {selectedSectionData.location_name} directly to enroll.</p>
                            </div>
                          )
                        ) : (
                          <>
                            {selectedSectionData && selectedSectionData.enrolled_count >= selectedSectionData.capacity ? (
                              <div className="space-y-3" data-testid="session-full-section">
                                <Alert className="border-amber-200 bg-amber-50">
                                  <AlertCircle className="h-4 w-4 text-amber-600" />
                                  <AlertDescription className="text-amber-700 text-sm">
                                    This session is currently full. Leave your email and we'll reach out if a spot opens up.
                                  </AlertDescription>
                                </Alert>
                                <NotifyMeButton
                                  courseId={courseId}
                                  flavor={course.title}
                                  source="session_full_interest"
                                  successMsg="✓ Got it! We'll reach out if a spot opens up."
                                />
                                <div className="text-center pt-1">
                                  <Link
                                    to="/contact"
                                    className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                                    data-testid="contact-us-link"
                                  >
                                    Contact us directly →
                                  </Link>
                                </div>
                              </div>
                            ) : (
                              <Button
                                className="w-full"
                                size="lg"
                                onClick={handleAddToCart}
                                disabled={!selectedSection || selectedLearners.length === 0 || addingToCart}
                                data-testid="add-to-cart-btn"
                              >
                                {addingToCart ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <ShoppingCart className="h-4 w-4 mr-2" />
                                )}
                                {selectedLearners.length > 1
                                  ? `Add ${selectedLearners.length} Enrollments to Cart`
                                  : 'Add to Cart'
                                }
                              </Button>
                            )}
                          </>
                        )}
                          </>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
